import React, { JSX, useCallback, useEffect, useRef, useState } from 'react'
import _ from 'lodash'
import { OverlayTrigger, Tooltip } from 'react-bootstrap'
import i18n from 'live/services/i18n'
import globalNotification from 'live/services/global-notification'
import { Divisor, FixedWidthConfigSection, MainContainer, Section, SectionRow } from '../styles/common'
import { ChannelsConfigurationBtn } from '../styles/ActionButtonStyle'
import { KPISDrillingRegistryContextType } from '../../types/kpis-drilling/kpis-drilling-registry-type'
import { RigKpiOptions, Rigs, SelectOptions } from '../../types'
import {
    ChannelsConfigurationModal,
    ChannelsInterpolationBadge,
    EquipmentSelectSection,
    PERFORMANCE_AND_RIG_FLOOR_KPIS_METHODOLOGY,
    PERFORMANCE_KPIS_METHODOLOGY
} from '../common'
import {
    fetchPreset,
    fetchRigFloorRealtimeStatus,
    hasRealtimeConflict,
    startRigFloorRealtime,
    stopRigFloorRealtime
} from '../../services/api'
import { useEquipments } from '../../hooks/useEquipments'
import { usePopulateRigFloorConfig } from '../../hooks/usePopulateRigFloorConfig'
import { useRigFloorMethodologyLogic } from '../../hooks/useRigFloorMethodologyLogic'
import { saveRigFloorProcessConfig, secondsToMilliseconds } from '../../utils/functions'
import { useDisabledWhenNotSelectedMethodoly } from '../../hooks/useDisabledWhenNotSelectedMethodoly'

// "Rig Floor Equipments"/"Riser Equipments" group sub-header — Source Sans Pro Regular 16px/20px
const groupTitleStyle = { fontSize: '16px', lineHeight: '20px', fontWeight: 400 }

export const RealtimeRigFloorKpis = (props: KPISDrillingRegistryContextType): JSX.Element => {
    const { contextState } = props
    const {
        hasPermissionToEdit,
        methodologyOptions,
        savedKpiOptions,
        eventType,
        rigId,
        wellId,
        rigs,
        realtimeStatus,
        setSavedKpiOptions,
        handleStopRealtime,
        setRigUsingWellName,
        handleStartRealtime
    } = contextState?.realtime?.props || {}

    const prevProps = useRef(null)

    const { setShowRigRealtimeAlreadModal, selectedPreset, setIsActButtonDisabled, isActButtonDisabled } =
        contextState?.realtime?.state || {}

    const { selectedMethodology, setSelectedMethodology } = contextState?.realtime?.rigfloor || {} // maybe it not exist in a first time

    const { setAdditionalOutputEventName, setAdditionalOutputEventValue, theme } = contextState?.service

    const getSelectedMethod = (): SelectOptions | undefined => {
        return methodologyOptions?.find(method => method.value === savedKpiOptions?.realtimeMethod)
    }

    const [selectedRealtimeOption, setSelectedRealtimeOption] = useState<SelectOptions>(getSelectedMethod())
    const [selectedHydratongEquipment, setSelectedHydratongEquipment] = useState<SelectOptions>()
    const [selectedHydrarackerEquipment, setSelectedHydrarackerEquipment] = useState<SelectOptions>()
    const [selectedTopDriveEquipment, setSelectedTopDriveEquipment] = useState<SelectOptions>()
    const [selectedRrtEquipment, setSelectedRrtEquipment] = useState<SelectOptions>()
    const [selectedCatwalkEquipment, setSelectedCatwalkEquipment] = useState<SelectOptions>()

    const [isProducingData, setIsProducingData] = useState(false)
    const [isUsingRigFloorInterpolation, setIsUsingRigFloorInterpolation] = useState<boolean>(false)
    const [isRigFloorChannelsOpened, setIsRigFloorChannelsOpened] = useState<boolean>(false)
    const [isRigFloorRealtimeRunning, setIsRigFloorRealtimeRunning] = useState<boolean>(false)

    const timeoutIdRef = useRef<NodeJS.Timeout>(null)

    const [interpolationFrequency, setInterpolationFrequency] = useState(0)
    const [maxDeltaWithoutInterpolation, setMaxDeltaWithoutInterpolation] = useState(0)
    const [enableRigFloor, setEnableRigFloor] = useState<boolean>(false)

    // Riser channels config — independent from Rig Floor's (RS-12)
    const [isUsingRiserInterpolation, setIsUsingRiserInterpolation] = useState<boolean>(false)
    const [isRiserChannelsOpened, setIsRiserChannelsOpened] = useState<boolean>(false)
    const [riserInterpolationFrequency, setRiserInterpolationFrequency] = useState(0)
    const [riserMaxDeltaWithoutInterpolation, setRiserMaxDeltaWithoutInterpolation] = useState(0)

    const collapsed = Boolean(contextState?.realtime?.props?.collapsed)

    // used to know if the rigfloor is registered in context state and avoid infinite loop
    const registeredInContext = useRef<{
        context: boolean
        hydratong: boolean
        hydraracker: boolean
        topdrive: boolean
    }>({
        context: false,
        hydratong: false,
        hydraracker: false,
        topdrive: false
    })

    const { hydratongEquipments, hydrarackerEquipments, topDriveEquipments, rrtEquipments, catwalkEquipments } =
        useEquipments({
            rigId,
            eventType,
            setAdditionalOutputEventName,
            setAdditionalOutputEventValue
        })

    usePopulateRigFloorConfig({
        setSelectedHydratongEquipment,
        setSelectedHydrarackerEquipment,
        setSelectedTopDriveEquipment,
        setSelectedRrtEquipment,
        setSelectedCatwalkEquipment,
        setSelectedMethodology,
        setIsUsingRigFloorInterpolation,
        setInterpolationFrequency,
        setMaxDeltaWithoutInterpolation,
        setIsUsingRiserInterpolation,
        setRiserInterpolationFrequency,
        setRiserMaxDeltaWithoutInterpolation,
        assetId: rigId,
        assetType: 'rig',
        operationType: 'realtime',
        hydratongEquipments,
        hydrarackerEquipments,
        topDriveEquipments,
        rrtEquipments,
        catwalkEquipments,
        setEnableRigFloor,
        setContextState: props?.setContextState
    })

    useRigFloorMethodologyLogic({
        selectedMethodology,
        setEnableRigFloor,
        setIsActButtonDisabled,
        selectedHydratongEquipment,
        selectedHydrarackerEquipment,
        selectedTopDriveEquipment
    })

    useDisabledWhenNotSelectedMethodoly({
        selectedMethodology,
        setEnableRigFloor,
        setIsActButtonDisabled
    })

    const canUpdateFields = useCallback((): boolean => {
        return isProducingData === false
    }, [isProducingData])

    const handleSetIsRigFloorRealtimeRunning = useCallback(
        (value: boolean): void => {
            setIsRigFloorRealtimeRunning(value)

            props?.setContextState(prevState => ({
                ...prevState,
                realtime: {
                    ...prevState?.realtime,
                    rigfloor: {
                        ...prevState?.realtime?.rigfloor,
                        canUpdateFields,
                        isProducingData: value,
                        isRigFloorRealtimeRunning: value
                    }
                }
            }))
        },
        [canUpdateFields, props]
    )

    const stopKpisDrillingRealtimeQuery = useCallback((): void => {
        const saved: RigKpiOptions = {
            ...savedKpiOptions,
            isRealtimeRunning: false
        }
        setSavedKpiOptions(saved)
        handleStopRealtime(saved)
    }, [handleStopRealtime, savedKpiOptions, setSavedKpiOptions])

    const stopRigFloorRealtimeQuery = useCallback((): void => {
        stopRigFloorRealtime(rigId).then(() => {
            handleSetIsRigFloorRealtimeRunning(false)

            // there are components that use this information in the context state
            props?.setContextState(prevState => ({
                ...prevState,
                realtime: {
                    ...prevState?.realtime,
                    rigfloor: {
                        ...prevState?.realtime?.rigfloor,
                        canUpdateFields,
                        isProducingData: false,
                        isRigFloorRealtimeRunning: false
                    }
                }
            }))
        })
    }, [canUpdateFields, handleSetIsRigFloorRealtimeRunning, props, rigId])

    const handleStopProcess = useCallback(async (): Promise<void> => {
        // this function is executed moments after stopping the plugin-kpis-drilling,
        // which means that we may no longer have the realtime status of kpis-drilling,
        // so we should stop rigfloor without checking the status of kpis-drilling
        stopRigFloorRealtimeQuery()

        if (savedKpiOptions?.isRealtimeRunning) {
            stopKpisDrillingRealtimeQuery()
        }

        globalNotification.success(i18n('Real-time event production has been terminated'))
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        isRigFloorRealtimeRunning,
        savedKpiOptions?.isRealtimeRunning,
        stopKpisDrillingRealtimeQuery,
        stopRigFloorRealtimeQuery,
        isProducingData,
        props?.contextState?.realtime?.rigfloor?.isRigFloorRealtimeRunning
    ])

    const handleCloseModal = useCallback(() => {
        if (typeof setShowRigRealtimeAlreadModal === 'function') {
            setShowRigRealtimeAlreadModal(false)
        }
    }, [setShowRigRealtimeAlreadModal])

    const saveCurrentMethodology = useCallback(async (): Promise<void> => {
        await saveRigFloorProcessConfig({
            assetType: 'rig',
            assetId: rigId,
            operationType: 'realtime',
            hydratongEquipmentUUID: selectedHydratongEquipment?.value,
            hydrarackerEquipmentUUID: selectedHydrarackerEquipment?.value,
            topDriveEquipmentUUID: selectedTopDriveEquipment?.value,
            rrtEquipmentUUID: selectedRrtEquipment?.value,
            catwalkEquipmentUUID: selectedCatwalkEquipment?.value,
            rigFloorMethodology: selectedMethodology?.value,
            rigFloorUseInterpolation: isUsingRigFloorInterpolation ?? false,
            rigFloorInterpolationFrequency: interpolationFrequency ?? 0,
            rigFloorMaxDeltaWithoutInterpolation: maxDeltaWithoutInterpolation ?? 0,
            riserUseInterpolation: isUsingRiserInterpolation ?? false,
            riserInterpolationFrequency: riserInterpolationFrequency ?? 0,
            riserMaxDeltaWithoutInterpolation: riserMaxDeltaWithoutInterpolation ?? 0
        })
    }, [
        interpolationFrequency,
        isUsingRigFloorInterpolation,
        maxDeltaWithoutInterpolation,
        isUsingRiserInterpolation,
        riserInterpolationFrequency,
        riserMaxDeltaWithoutInterpolation,
        rigId,
        selectedHydrarackerEquipment?.value,
        selectedHydratongEquipment?.value,
        selectedMethodology?.value,
        selectedTopDriveEquipment?.value,
        selectedRrtEquipment?.value,
        selectedCatwalkEquipment?.value
    ])

    const executeKpisDrillingRealtimeQuery = useCallback(async (): Promise<void> => {
        hasRealtimeConflict(eventType, rigId, wellId)
            .then(async response => {
                const [rigUsingWell] = (rigs as Rigs[])?.filter(rig => rig.id === response.rig)
                if (rigUsingWell) {
                    setRigUsingWellName(rigUsingWell.name)

                    if (typeof setShowRigRealtimeAlreadModal === 'function') {
                        setShowRigRealtimeAlreadModal(true)
                    }
                } else {
                    await fetchPreset(rigId, selectedPreset?.value).then(async res => {
                        if (res.ok) {
                            const data = await res.json()
                            if (selectedRealtimeOption) {
                                const saved: RigKpiOptions = {
                                    ...savedKpiOptions,
                                    preset: data,
                                    lastRealtime: Date.now().toString()
                                }
                                setSavedKpiOptions(saved)
                                handleStartRealtime(saved)
                            }
                        }
                    })
                }
            })
            .catch(error => {
                console.log('Error occurred during API call:', error)
            })
    }, [
        eventType,
        handleStartRealtime,
        rigId,
        rigs,
        savedKpiOptions,
        selectedPreset?.value,
        selectedRealtimeOption,
        setRigUsingWellName,
        setSavedKpiOptions,
        setShowRigRealtimeAlreadModal,
        wellId
    ])

    const rigFloorConfigRef = useRef<{
        interpolationFrequency: number
        maxDeltaWithoutInterpolation: number
        isUsingRigFloorInterpolation: boolean
        riserInterpolationFrequency: number
        riserMaxDeltaWithoutInterpolation: number
        isUsingRiserInterpolation: boolean
    }>({
        interpolationFrequency: 0,
        maxDeltaWithoutInterpolation: 0,
        isUsingRigFloorInterpolation: false,
        riserInterpolationFrequency: 0,
        riserMaxDeltaWithoutInterpolation: 0,
        isUsingRiserInterpolation: false
    })

    useEffect(() => {
        rigFloorConfigRef.current = {
            interpolationFrequency,
            maxDeltaWithoutInterpolation,
            isUsingRigFloorInterpolation,
            riserInterpolationFrequency,
            riserMaxDeltaWithoutInterpolation,
            isUsingRiserInterpolation
        }
    }, [
        interpolationFrequency,
        maxDeltaWithoutInterpolation,
        isUsingRigFloorInterpolation,
        riserInterpolationFrequency,
        riserMaxDeltaWithoutInterpolation,
        isUsingRiserInterpolation
    ])

    const executeRigFloorRealtimeQuery = useCallback(async (): Promise<void> => {
        const currentConfig = rigFloorConfigRef.current
        const res = await saveRigFloorProcessConfig({
            assetType: 'rig',
            assetId: rigId,
            operationType: 'realtime',
            hydratongEquipmentUUID: selectedHydratongEquipment?.value,
            hydrarackerEquipmentUUID: selectedHydrarackerEquipment?.value,
            topDriveEquipmentUUID: selectedTopDriveEquipment?.value,
            rrtEquipmentUUID: selectedRrtEquipment?.value,
            catwalkEquipmentUUID: selectedCatwalkEquipment?.value,
            rigFloorMethodology: selectedMethodology?.value,
            rigFloorUseInterpolation: currentConfig.isUsingRigFloorInterpolation ?? false,
            rigFloorInterpolationFrequency: currentConfig.interpolationFrequency ?? 0,
            rigFloorMaxDeltaWithoutInterpolation: currentConfig.maxDeltaWithoutInterpolation ?? 0,
            riserUseInterpolation: currentConfig.isUsingRiserInterpolation ?? false,
            riserInterpolationFrequency: currentConfig.riserInterpolationFrequency ?? 0,
            riserMaxDeltaWithoutInterpolation: currentConfig.riserMaxDeltaWithoutInterpolation ?? 0
        })

        if (res.ok) {
            const { hydrarackerEquipmentUUID, hydratongEquipmentUUID, topDriveEquipmentUUID, rrtEquipmentUUID, catwalkEquipmentUUID } =
                await res.json()
            const startRes = await startRigFloorRealtime(
                rigId,
                currentConfig.isUsingRigFloorInterpolation,
                secondsToMilliseconds(currentConfig.interpolationFrequency),
                secondsToMilliseconds(currentConfig.maxDeltaWithoutInterpolation),
                hydratongEquipmentUUID,
                hydrarackerEquipmentUUID,
                topDriveEquipmentUUID,
                rrtEquipmentUUID,
                catwalkEquipmentUUID
            )

            if (startRes.ok) {
                handleSetIsRigFloorRealtimeRunning(true)
            }
        }
    }, [
        handleSetIsRigFloorRealtimeRunning,
        rigId,
        selectedHydrarackerEquipment?.value,
        selectedHydratongEquipment?.value,
        selectedMethodology?.value,
        selectedTopDriveEquipment?.value,
        selectedRrtEquipment?.value,
        selectedCatwalkEquipment?.value
    ])

    const handleStartProcess = useCallback(async (): Promise<void> => {
        if (typeof setIsActButtonDisabled === 'function') {
            setIsActButtonDisabled(true)
        }

        if (selectedMethodology?.value === PERFORMANCE_AND_RIG_FLOOR_KPIS_METHODOLOGY) {
            await executeKpisDrillingRealtimeQuery()
            await executeRigFloorRealtimeQuery()
            globalNotification.success(i18n('Performance and RIG Floor KPIs production started successfully!'), 5)
        } else if (selectedMethodology?.value === PERFORMANCE_KPIS_METHODOLOGY) {
            await saveCurrentMethodology()
            await executeKpisDrillingRealtimeQuery()
            globalNotification.success(i18n('Performance KPIs production started successfully!'), 5)
        }

        if (typeof setIsActButtonDisabled === 'function') {
            setIsActButtonDisabled(false)
        }
    }, [
        executeKpisDrillingRealtimeQuery,
        executeRigFloorRealtimeQuery,
        saveCurrentMethodology,
        selectedMethodology?.value,
        setIsActButtonDisabled
    ])

    const updatePropsContextState = (): void => {
        if (typeof props?.setContextState === 'function' && !_.isEmpty(props?.contextState?.realtime?.state)) {
            props.setContextState(prevState => ({
                ...prevState,
                realtime: {
                    ...prevState?.realtime,
                    handleStartActionCallback: handleStartProcess,
                    handleStopActionCallback: handleStopProcess,
                    handleCloseModalCallback: handleCloseModal,
                    rigfloor: {
                        ...prevState?.realtime?.rigfloor,
                        canUpdateFields,
                        isProducingData
                    }
                }
            }))

            prevProps.current = props
        }
    }

    // update context state when props change from kpis drilling
    const handleKpisDrillingPropsChange = useCallback((): void => {
        if (prevProps != null && JSON.stringify(prevProps.current) === JSON.stringify(props)) {
            return
        }
        updatePropsContextState()
    }, [props])

    // update context state with handlers and rig floor functions
    const addFunctionsToContext = useCallback((): void => {
        updatePropsContextState()
    }, [
        wellId,
        rigId,
        rigs,
        interpolationFrequency,
        isUsingRigFloorInterpolation,
        maxDeltaWithoutInterpolation,
        isProducingData,
        selectedHydrarackerEquipment?.value,
        selectedHydratongEquipment?.value,
        selectedMethodology?.value,
        selectedTopDriveEquipment?.value,
        selectedRrtEquipment?.value,
        selectedCatwalkEquipment?.value
    ])

    useEffect(() => {
        handleKpisDrillingPropsChange()
    }, [handleKpisDrillingPropsChange])

    useEffect(() => {
        addFunctionsToContext()
    }, [addFunctionsToContext])

    useEffect(() => {
        if (typeof setSavedKpiOptions === 'function') {
            setSavedKpiOptions({
                ...savedKpiOptions,
                realtimeMethod: selectedRealtimeOption?.value
            })
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedRealtimeOption])

    useEffect(() => {
        const localIsProducingData = isRigFloorRealtimeRunning || savedKpiOptions?.isRealtimeRunning

        setIsProducingData(localIsProducingData)

        props?.setContextState(prevState => ({
            ...prevState,
            realtime: {
                ...prevState?.realtime,
                rigfloor: {
                    ...prevState?.realtime?.rigfloor,
                    canUpdateFields,
                    isProducingData: localIsProducingData
                }
            }
        }))
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [canUpdateFields, isRigFloorRealtimeRunning, savedKpiOptions?.isRealtimeRunning])

    useEffect(() => {
        let intervalId: NodeJS.Timeout | null = null
        let isCancelled = false

        const pollStatus = async (): Promise<void> => {
            try {
                const res = await fetchRigFloorRealtimeStatus(rigId)
                if (isCancelled) return
                if (res.ok) {
                    const data = await res.json()
                    if (data.status === 'running') {
                        handleSetIsRigFloorRealtimeRunning(true)
                    } else {
                        handleSetIsRigFloorRealtimeRunning(false)
                    }
                }
            } catch (err) {
                console.error('Polling error:', err)
            }
        }

        pollStatus()

        if (isRigFloorRealtimeRunning) {
            intervalId = setInterval(pollStatus, 3000)
        }

        return () => {
            isCancelled = true
            if (intervalId) clearInterval(intervalId)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [eventType, isRigFloorRealtimeRunning])

    useEffect(() => {
        if (!realtimeStatus) return

        const localRealtimeStatus = realtimeStatus?.toLowerCase()

        if (localRealtimeStatus !== 'running' && localRealtimeStatus !== 'error' && !timeoutIdRef.current) {
            setIsActButtonDisabled(true)
            const timeoutSignal = setTimeout(() => {
                setIsActButtonDisabled(false)
            }, 15000)
            timeoutIdRef.current = timeoutSignal
        }
        if (localRealtimeStatus === 'running' || (localRealtimeStatus === 'running' && isActButtonDisabled)) {
            setIsActButtonDisabled(false)
            clearTimeout(timeoutIdRef.current)
            timeoutIdRef.current = null
        }

        return () => {
            clearTimeout(timeoutIdRef.current)
            timeoutIdRef.current = null
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [realtimeStatus, isActButtonDisabled])

    useEffect(() => {
        setSelectedRealtimeOption(getSelectedMethod())
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [methodologyOptions, savedKpiOptions])

    return (
        <>
            <MainContainer>
                {!collapsed && (
                    <div className="title">
                        <span style={groupTitleStyle}>{i18n('Rig Floor Equipments')}</span>
                    </div>
                )}
                <SectionRow className="section-row-top">
                    <EquipmentSelectSection
                        title="Hydratong"
                        titleClassName="kpi-generation-methodology"
                        tooltipId="kpi-generation-methodology-info-tooltip"
                        tooltipText="Here you will see the equipment associated with this RIG and a Hydratong archetype."
                        dataTestId="selector-realtime-kpi-generation-methodology"
                        linkColor={theme['link-color']}
                        linkClassName="equipment-link"
                        disabled={!canUpdateFields() || !hasPermissionToEdit || !enableRigFloor}
                        readOnly={collapsed}
                        options={hydratongEquipments}
                        value={selectedHydratongEquipment}
                        onChange={setSelectedHydratongEquipment}
                    />
                    <EquipmentSelectSection
                        title="Hydraracker"
                        titleClassName="rig-presets"
                        tooltipId="rig-presets-info-tooltip"
                        tooltipText="Here you will see the equipment associated with this RIG and a Hydraracker archetype."
                        dataTestId="selector-realtime-rig-presets"
                        linkColor={theme['link-color']}
                        linkClassName="equipment-link"
                        disabled={!canUpdateFields() || !hasPermissionToEdit || !enableRigFloor}
                        readOnly={collapsed}
                        options={hydrarackerEquipments}
                        value={selectedHydrarackerEquipment}
                        onChange={setSelectedHydrarackerEquipment}
                    />
                    <EquipmentSelectSection
                        title="Top Drive"
                        titleClassName="rig-presets"
                        tooltipId="rig-presets-info-tooltip"
                        tooltipText="Here you will see the equipment associated with this RIG and a Top Drive archetype."
                        dataTestId="selector-realtime-rig-presets"
                        linkColor={theme['link-color']}
                        linkClassName="equipment-link"
                        disabled={!canUpdateFields() || !hasPermissionToEdit || !enableRigFloor}
                        readOnly={collapsed}
                        options={topDriveEquipments}
                        value={selectedTopDriveEquipment}
                        onChange={setSelectedTopDriveEquipment}
                    />
                    {/* Rig Floor channels config */}
                    <Section>
                        <div className="section-name">
                            {collapsed && <span>{i18n('Rig Floor channels config')}</span>}
                        </div>
                        {collapsed ? null : isProducingData ? (
                            <OverlayTrigger
                                placement="top"
                                overlay={
                                    <Tooltip style={{ zIndex: 9999 }} id={'channels-configurations-btn-tooltip'}>
                                        {i18n(
                                            "It's not possible to configure channels while Real Time is Running. You need to stop Real Time to access Channels Configurations."
                                        )}
                                    </Tooltip>
                                }
                            >
                                <ChannelsConfigurationBtn
                                    id="kpi-options_realtime-channels-configuration"
                                    enabled={hasPermissionToEdit && canUpdateFields()}
                                    onClick={() => {
                                        if (canUpdateFields()) {
                                            setIsRigFloorChannelsOpened(true)
                                        }
                                    }}
                                >
                                    {i18n('Rig Floor channels config')}
                                </ChannelsConfigurationBtn>
                            </OverlayTrigger>
                        ) : (
                            <ChannelsConfigurationBtn
                                id="kpi-options_realtime-channels-configuration"
                                enabled={canUpdateFields() && enableRigFloor && hasPermissionToEdit}
                                onClick={() => {
                                    if (canUpdateFields() && enableRigFloor) {
                                        setIsRigFloorChannelsOpened(true)
                                    }
                                }}
                            >
                                {i18n('Rig Floor channels config')}
                            </ChannelsConfigurationBtn>
                        )}
                        <ChannelsInterpolationBadge active={isUsingRigFloorInterpolation} collapsed={collapsed} />
                    </Section>
                </SectionRow>
            </MainContainer>
            <Divisor />
            <MainContainer>
                {!collapsed && (
                    <div className="title">
                        <span style={groupTitleStyle}>{i18n('Riser Equipments')}</span>
                    </div>
                )}
                <SectionRow className="section-row-top">
                    <EquipmentSelectSection
                        title="RRT"
                        titleClassName="rig-presets"
                        tooltipId="rrt-presets-info-tooltip"
                        tooltipText="Here you will see the equipment associated with this RIG and a RRT archetype."
                        dataTestId="selector-realtime-rrt"
                        linkColor={theme['link-color']}
                        linkClassName="equipment-link"
                        disabled={!canUpdateFields() || !hasPermissionToEdit || !enableRigFloor}
                        readOnly={collapsed}
                        options={rrtEquipments}
                        value={selectedRrtEquipment}
                        onChange={setSelectedRrtEquipment}
                    />
                    <EquipmentSelectSection
                        title="Catwalk"
                        titleClassName="rig-presets"
                        tooltipId="catwalk-presets-info-tooltip"
                        tooltipText="Here you will see the equipment associated with this RIG and a Catwalk archetype."
                        dataTestId="selector-realtime-catwalk"
                        linkColor={theme['link-color']}
                        linkClassName="equipment-link"
                        disabled={!canUpdateFields() || !hasPermissionToEdit || !enableRigFloor}
                        readOnly={collapsed}
                        options={catwalkEquipments}
                        value={selectedCatwalkEquipment}
                        onChange={setSelectedCatwalkEquipment}
                    />
                    {/* Riser channels config */}
                    <FixedWidthConfigSection>
                        <div className="section-name">
                            {collapsed && <span>{i18n('Riser channels config')}</span>}
                        </div>
                        {collapsed ? null : isProducingData ? (
                            <OverlayTrigger
                                placement="top"
                                overlay={
                                    <Tooltip style={{ zIndex: 9999 }} id={'riser-channels-configurations-btn-tooltip'}>
                                        {i18n(
                                            "It's not possible to configure channels while Real Time is Running. You need to stop Real Time to access Channels Configurations."
                                        )}
                                    </Tooltip>
                                }
                            >
                                <ChannelsConfigurationBtn
                                    id="kpi-options_realtime-riser-channels-configuration"
                                    enabled={hasPermissionToEdit && canUpdateFields()}
                                    onClick={() => {
                                        if (canUpdateFields()) {
                                            setIsRiserChannelsOpened(true)
                                        }
                                    }}
                                >
                                    {i18n('Riser channels config')}
                                </ChannelsConfigurationBtn>
                            </OverlayTrigger>
                        ) : (
                            <ChannelsConfigurationBtn
                                id="kpi-options_realtime-riser-channels-configuration"
                                enabled={canUpdateFields() && enableRigFloor && hasPermissionToEdit}
                                onClick={() => {
                                    if (canUpdateFields() && enableRigFloor) {
                                        setIsRiserChannelsOpened(true)
                                    }
                                }}
                            >
                                {i18n('Riser channels config')}
                            </ChannelsConfigurationBtn>
                        )}
                        <ChannelsInterpolationBadge active={isUsingRiserInterpolation} collapsed={collapsed} />
                    </FixedWidthConfigSection>
                </SectionRow>
            </MainContainer>
            {isRigFloorChannelsOpened && (
                <ChannelsConfigurationModal
                    title={i18n('Rig Floor channels configurations')}
                    isOpen={isRigFloorChannelsOpened}
                    setIsOpen={setIsRigFloorChannelsOpened}
                    setIsUsingInterpolation={setIsUsingRigFloorInterpolation}
                    externalSetInterpolationFrequency={setInterpolationFrequency}
                    externalSetMaxDeltaWithoutInterpolation={setMaxDeltaWithoutInterpolation}
                    currentConfig={{
                        useInterpolation: isUsingRigFloorInterpolation,
                        interpolationFrequency: String(interpolationFrequency),
                        maxDeltaWithoutInterpolation: String(maxDeltaWithoutInterpolation)
                    }}
                />
            )}
            {isRiserChannelsOpened && (
                <ChannelsConfigurationModal
                    title={i18n('Riser channels configurations')}
                    isOpen={isRiserChannelsOpened}
                    setIsOpen={setIsRiserChannelsOpened}
                    setIsUsingInterpolation={setIsUsingRiserInterpolation}
                    externalSetInterpolationFrequency={setRiserInterpolationFrequency}
                    externalSetMaxDeltaWithoutInterpolation={setRiserMaxDeltaWithoutInterpolation}
                    currentConfig={{
                        useInterpolation: isUsingRiserInterpolation,
                        interpolationFrequency: String(riserInterpolationFrequency),
                        maxDeltaWithoutInterpolation: String(riserMaxDeltaWithoutInterpolation)
                    }}
                />
            )}
        </>
    )
}
